import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { loadHistory, saveConversation, deleteConversation } from '../utils/aiHistoryDb'

/**
 * Repli affiché tant que Drupal n'a pas répondu, et si l'endpoint est en
 * erreur. La liste réelle est déclarée dans `orbit_ai_chat.settings` (contexte
 * → `paths`), jamais dans le YAML d'une page.
 */
export const DEFAULT_AI_CONTEXTS = [
  {
    id: 'all_data',
    label: 'Toutes les données',
    description: "Discutez avec l'IA sur l'ensemble des données Orbit",
  },
]

// sessionStorage : état de l'onglet courant (conversation active, isOpen)
const SESSION_KEY = 'orbit_ai_chat'

function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveSession(data) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data))
  } catch {
    // Ignore (quota exceeded, private browsing, etc.)
  }
}

function generateConvId() {
  return `conv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
}

export const AiChatContext = createContext(null)

export function AiChatProvider({ children }) {
  const persisted = loadSession()

  const { pathname, search } = useLocation()
  const currentPath = `${pathname}${search}`

  const [isOpen, setIsOpen] = useState(persisted.isOpen ?? false)
  const [availableContexts, setAvailableContextsState] = useState(DEFAULT_AI_CONTEXTS)
  const [selectedContext, setSelectedContext] = useState(persisted.selectedContext ?? null)
  const [messages, setMessages] = useState(persisted.messages ?? [])
  const [sessionId, setSessionIdState] = useState(persisted.sessionId ?? null)
  const [currentConvId, setCurrentConvId] = useState(persisted.currentConvId ?? null)

  // Les options du panel suivent la page : c'est Drupal qui décide, à partir
  // du chemin courant et des `paths` déclarés en config.
  useEffect(() => {
    const controller = new AbortController()
    fetch(`/api/ai-chat/contexts?path=${encodeURIComponent(currentPath)}`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (Array.isArray(data?.contexts)) setAvailableContextsState(data.contexts)
      })
      .catch(() => {
        // Endpoint injoignable : on garde le repli plutôt qu'un panel vide.
      })
    return () => controller.abort()
  }, [currentPath])

  // Historique chargé de manière asynchrone depuis IndexedDB
  const [history, setHistory] = useState([])
  useEffect(() => {
    loadHistory().then(setHistory).catch(() => {})
  }, [])

  // Sync état courant dans sessionStorage (onglet, rapide)
  useEffect(() => {
    saveSession({ isOpen, selectedContext, messages, sessionId, currentConvId })
  }, [isOpen, selectedContext, messages, sessionId, currentConvId])

  // Auto-save dans IndexedDB à chaque évolution de la conversation
  useEffect(() => {
    if (!messages.length || !selectedContext || !sessionId || !currentConvId) return
    const firstUserMsg = messages.find(m => m.role === 'user')
    if (!firstUserMsg) return

    const startedAt = messages[0]?.timestamp
      ? new Date(messages[0].timestamp).toISOString()
      : new Date().toISOString()

    const entry = {
      id: currentConvId,
      sessionId,
      contextId: selectedContext.id,
      contextLabel: selectedContext.label,
      firstQuestion: firstUserMsg.content.slice(0, 100),
      startedAt,
      updatedAt: new Date().toISOString(),
      messages,
    }

    saveConversation(entry).catch(() => {})

    // Mise à jour optimiste de l'état en mémoire
    setHistory(prev => {
      const filtered = prev.filter(e => e.id !== currentConvId)
      return [entry, ...filtered]
    })
  }, [messages, sessionId, currentConvId]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => setIsOpen(v => !v)

  const selectContext = (ctx) => {
    setSelectedContext(ctx)
    setCurrentConvId(generateConvId())
  }

  const addMessage = (msg) => setMessages(prev => [...prev, msg])

  const setSessionId = (id) => setSessionIdState(id)

  const resetConversation = () => {
    setSelectedContext(null)
    setMessages([])
    setSessionIdState(null)
    setCurrentConvId(null)
  }

  // Repart d'une conversation vierge dans le même contexte (message d'accueil + nouvelle session)
  const startNewConversation = () => {
    setMessages([])
    setSessionIdState(null)
    setCurrentConvId(generateConvId())
  }

  // Reprend une conversation depuis l'historique
  const resumeConversation = (entry) => {
    setSelectedContext({ id: entry.contextId, label: entry.contextLabel, description: '' })
    setMessages(entry.messages ?? [])
    setSessionIdState(entry.sessionId)
    setCurrentConvId(entry.id)
  }

  // Supprime une entrée de l'historique (IndexedDB + état en mémoire)
  const deleteHistoryEntry = (id) => {
    deleteConversation(id).catch(() => {})
    setHistory(prev => prev.filter(e => e.id !== id))
  }

  return (
    <AiChatContext.Provider value={{
      isOpen,
      availableContexts,
      selectedContext,
      currentConvId,
      currentPath,
      messages,
      sessionId,
      history,
      toggle,
      selectContext,
      addMessage,
      setSessionId,
      resetConversation,
      startNewConversation,
      resumeConversation,
      deleteHistoryEntry,
    }}>
      {children}
    </AiChatContext.Provider>
  )
}

export function useAiChat() {
  const ctx = useContext(AiChatContext)
  if (ctx === null) {
    throw new Error('useAiChat must be used within an AiChatProvider')
  }
  return ctx
}
